// ============================================================
// logic.naci.tech — Landing.tsx v2.0
// V Naming Layer: "Catch The Pattern™ — An EMET / ELLARI System"
// Palette: shopellari-tokens.css (imported via index.html)
// Font stack: var(--font-serif) / var(--font-sans) / var(--font-mono)
// ISO: 2026-05-06
// ============================================================

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── Sample analysis reveal (live demo on landing) ──────────────────────────
const DEMO_SENTENCES = [
  "Most people know what they mean even when they cannot say it.",
  "The policy protects the institution, not the individual.",
  "She said she understood, but her actions suggested otherwise.",
  "This contract is binding except where prohibited by law.",
  "Progress was made on several key issues during the meeting.",
];

const DEMO_VERDICTS = [
  { verdict: "VALID WITH QUALIFICATION", color: "#d6b168" },
  { verdict: "STRUCTURALLY EVASIVE",     color: "#b94a48" },
  { verdict: "AMBIGUOUS — REQUIRES CONTEXT", color: "#d6b168" },
  { verdict: "VALID — HIDDEN SCOPE LIMIT",   color: "#d6b168" },
  { verdict: "PASSIVE CONSTRUCTION — AGENT OBSCURED", color: "#b94a48" },
];

// ── Tier definitions ────────────────────────────────────────────────────────
const TIERS = [
  {
    key:    "catch_the_pattern_pro",
    name:   "Catch The Pattern™ Pro",
    price:  "$12/mo",
    label:  "PRO",
    desc:   "Five analysis modes, six levels, persistent vocabulary ledger. The pattern was always there. Now you'll see it.",
    items:  [
      "Solo analysis (single sentence, 8-step breakdown)",
      "Challenge mode (6 levels, ELO-based progression)",
      "Sparring mode (consecutive-sentence argument chains)",
      "Import mode (multi-paragraph + document paste)",
      "Vocabulary ledger (persistent across sessions)",
    ],
    cta:     "Activate Catch The Pattern™ Pro",
    stripeKey: "catch_the_pattern_pro",
  },
  {
    key:    "pattern_glow_scholar",
    name:   "Pattern Glow™ Scholar",
    price:  "$29/mo",
    label:  "SCHOLAR",
    desc:   "Everything in Pro, plus the 30-day guided Pattern Glow path, day tracking, personal cognitive precision report at day 30, and API access.",
    items:  [
      "All Catch The Pattern™ Pro features",
      "Pattern Glow™ 30-day guided path",
      "Day-by-day cognitive challenge structure",
      "Pattern Glow Report generated at day 30",
      "API access for organizational use",
      "Roots mode: Semitic root derivation + etymology",
    ],
    cta:     "Activate Pattern Glow™ Scholar",
    stripeKey: "pattern_glow_scholar",
    featured: true,
  },
];

// ── Five modes ──────────────────────────────────────────────────────────────
const MODES = [
  {
    id:    "solo",
    name:  "Solo",
    glyph: "◎",
    desc:  "One sentence. 8-step breakdown. Logical form, presuppositions, hidden assumptions, argument strength, verdict.",
  },
  {
    id:    "challenge",
    name:  "Challenge",
    glyph: "◆",
    desc:  "Six difficulty levels with ELO progression. Sentences grow in structural complexity. Your ledger grows with them.",
  },
  {
    id:    "sparring",
    name:  "Sparring",
    glyph: "◈",
    desc:  "Consecutive-sentence arguments. Track how claims build, shift, or contradict across a chain of assertions.",
  },
  {
    id:    "import",
    name:  "Import",
    glyph: "◇",
    desc:  "Paste entire documents, inspection reports, contracts, or arguments. EMET maps the full structural landscape.",
  },
  {
    id:    "roots",
    name:  "Roots \u05D0",
    glyph: "\u05D0",
    desc:  "Semitic root derivation and etymology. Named concepts through morphological analysis. IP naming research via formal structure.",
  },
];

// ── Trust marks ─────────────────────────────────────────────────────────────
const TRUST = [
  { label: "EMET Verified",      desc: "Analysis engine trained on formal logic, not pattern matching" },
  { label: "ELLARI System",      desc: "Built within the ELEOS product ecosystem" },
  { label: "Versioned Artifact", desc: "Every analysis mode is documented and maintained" },
  { label: "Evidence-Ready",     desc: "Output designed for real decision and documentation environments" },
];

// ────────────────────────────────────────────────────────────────────────────
export default function Landing() {
  const [demoIdx, setDemoIdx]     = useState(0);
  const [revealed, setRevealed]   = useState(false);
  const [activeTier, setActiveTier] = useState<string | null>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setDemoIdx(i => (i + 1) % DEMO_SENTENCES.length);
      setRevealed(false);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const SE = {
    cream:  "var(--se-cream, #f9f6f1)",
    black:  "var(--se-black, #1a1a1a)",
    gold:   "var(--se-gold, #d6b168)",
    soft:   "var(--se-soft-gray, #e6e2dc)",
    mid:    "var(--se-mid-gray, #b8b3ac)",
    deep:   "var(--se-deep-charcoal, #2a2a2a)",
    serif:  "var(--font-serif, 'Cormorant Garamond', Georgia, serif)",
    sans:   "var(--font-sans, 'Inter', system-ui, sans-serif)",
    mono:   "var(--font-mono, 'IBM Plex Mono', 'DM Mono', monospace)",
    logic:  "var(--accent-logic, #534AB7)",
  };

  return (
    <div style={{ background: SE.cream, fontFamily: SE.sans, color: SE.black }}>

      {/* ── NAV ────────────────────────────────────────────────────────── */}
      <nav style={{
        background: SE.black, borderBottom: `1px solid ${SE.deep}`,
        padding: "0 32px", display: "flex", alignItems: "center",
        justifyContent: "space-between", height: "52px", position: "sticky", top: 0, zIndex: 100,
      }}>
        <span style={{
          fontFamily: SE.mono, fontSize: "0.7rem", letterSpacing: "0.18em",
          color: SE.gold, textTransform: "uppercase",
        }}>
          CATCH THE PATTERN™ — EMET
        </span>
        <div style={{ display: "flex", gap: "24px", alignItems: "center" }}>
          {["Modes", "Pricing", "Roots"].map(label => (
            <a key={label} href={`#${label.toLowerCase()}`} style={{
              color: SE.mid, fontFamily: SE.sans, fontSize: "0.8rem",
              textDecoration: "none", letterSpacing: "0.04em",
            }}>{label}</a>
          ))}
          <Link to="/app" style={{
            background: SE.gold, color: SE.black, padding: "7px 18px",
            fontFamily: SE.sans, fontWeight: 600, fontSize: "0.78rem",
            textDecoration: "none", borderRadius: "2px", letterSpacing: "0.04em",
          }}>
            Open App
          </Link>
        </div>
      </nav>

      {/* ── HERO ───────────────────────────────────────────────────────── */}
      <section style={{
        background: SE.black, color: SE.cream,
        padding: "96px 32px 80px", textAlign: "center",
      }}>
        <p style={{
          fontFamily: SE.mono, fontSize: "0.62rem", letterSpacing: "0.2em",
          color: SE.gold, textTransform: "uppercase", marginBottom: "20px",
        }}>
          AN EMET / ELLARI VENTURES SYSTEM — logic.naci.tech
        </p>
        <h1 style={{
          fontFamily: SE.serif, fontSize: "clamp(2.6rem, 5.5vw, 4.2rem)",
          fontWeight: 300, maxWidth: "760px", margin: "0 auto 20px", lineHeight: 1.15,
          letterSpacing: "-0.01em",
        }}>
          Formal Logic Training<br />for the Language-Dense World.
        </h1>
        <p style={{
          color: SE.mid, fontSize: "1.05rem", maxWidth: "560px",
          margin: "0 auto 40px", lineHeight: 1.75,
        }}>
          Five analysis modes. Six difficulty levels. A vocabulary ledger that grows
          with every session. The pattern was always there — now you will see it.
        </p>
        <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
          <Link to="/app" style={{
            background: SE.gold, color: SE.black,
            padding: "14px 36px", fontFamily: SE.sans, fontWeight: 700,
            fontSize: "0.875rem", textDecoration: "none", borderRadius: "2px",
            letterSpacing: "0.05em",
          }}>
            Analyze A Sentence →
          </Link>
          <a href="#pricing" style={{
            border: `1px solid ${SE.mid}`, color: SE.cream,
            padding: "14px 36px", fontFamily: SE.sans, fontSize: "0.875rem",
            textDecoration: "none", borderRadius: "2px", letterSpacing: "0.04em",
          }}>
            View Plans
          </a>
        </div>
      </section>

      {/* ── LIVE DEMO ──────────────────────────────────────────────────── */}
      <section style={{ padding: "72px 32px", maxWidth: "860px", margin: "0 auto" }}>
        <Label label="LIVE DEMO" />
        <h2 style={{
          fontFamily: SE.serif, fontSize: "clamp(1.5rem, 3vw, 2rem)",
          fontWeight: 400, margin: "16px 0 32px",
        }}>
          Every sentence contains structure. EMET surfaces it.
        </h2>

        {/* Demo card */}
        <div style={{
          background: SE.black, borderRadius: "4px", padding: "32px",
          border: `1px solid ${SE.deep}`,
        }}>
          <p style={{ fontFamily: SE.mono, fontSize: "0.6rem", color: SE.gold, letterSpacing: "0.14em", marginBottom: "16px" }}>
            INPUT SENTENCE
          </p>
          <p style={{
            fontFamily: SE.serif, fontSize: "1.3rem", color: SE.cream,
            lineHeight: 1.5, marginBottom: "24px",
          }}>
            "{DEMO_SENTENCES[demoIdx]}"
          </p>

          {!revealed ? (
            <button
              onClick={() => setRevealed(true)}
              style={{
                background: "transparent", border: `1px solid ${SE.gold}`,
                color: SE.gold, padding: "10px 24px", fontFamily: SE.mono,
                fontSize: "0.7rem", letterSpacing: "0.12em", cursor: "pointer",
                borderRadius: "2px", textTransform: "uppercase",
              }}
            >
              Reveal Analysis →
            </button>
          ) : (
            <div>
              <p style={{ fontFamily: SE.mono, fontSize: "0.6rem", color: SE.mid, letterSpacing: "0.12em", marginBottom: "10px" }}>
                VERDICT
              </p>
              <p style={{
                fontFamily: SE.mono, fontSize: "0.85rem",
                color: DEMO_VERDICTS[demoIdx].color,
                letterSpacing: "0.08em", fontWeight: 600,
              }}>
                {DEMO_VERDICTS[demoIdx].verdict}
              </p>
              <p style={{ color: SE.mid, fontSize: "0.82rem", marginTop: "12px", lineHeight: 1.6 }}>
                Full 8-step breakdown — logical form, presuppositions, hidden assumptions,
                argument strength, failure modes, and vocabulary expansion — available in the app.
              </p>
            </div>
          )}
        </div>

        <p style={{ fontFamily: SE.mono, fontSize: "0.65rem", color: SE.mid, marginTop: "16px", letterSpacing: "0.08em" }}>
          Auto-cycles every 5 seconds. Click to reveal early.
        </p>
      </section>

      <Divider />

      {/* ── MODES ──────────────────────────────────────────────────────── */}
      <section id="modes" style={{ padding: "72px 32px", maxWidth: "1200px", margin: "0 auto" }}>
        <Label label="FIVE ANALYSIS MODES" />
        <h2 style={{ fontFamily: SE.serif, fontSize: "clamp(1.5rem, 3vw, 2.1rem)", fontWeight: 400, margin: "16px 0 8px" }}>
          Every mode trains a different dimension of pattern recognition.
        </h2>
        <p style={{ color: SE.mid, fontSize: "0.9rem", marginBottom: "40px", maxWidth: "600px" }}>
          The modes build on each other. Solo is the foundation. Import is the floor.
          Use them in sequence.
        </p>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          gap: "16px",
        }}>
          {MODES.map(mode => (
            <div key={mode.id} style={{
              background: "#fff",
              border: `1px solid ${SE.soft}`,
              borderRadius: "4px", padding: "24px",
              borderTop: `3px solid ${SE.gold}`,
            }}>
              <div style={{
                fontFamily: SE.mono, fontSize: mode.id === "roots" ? "1.4rem" : "1.1rem",
                color: SE.gold, marginBottom: "12px",
              }}>
                {mode.glyph}
              </div>
              <div style={{ fontFamily: SE.serif, fontSize: "1.05rem", marginBottom: "8px" }}>
                {mode.name}
              </div>
              <div style={{ fontSize: "0.8rem", color: SE.mid, lineHeight: 1.6 }}>
                {mode.desc}
              </div>
            </div>
          ))}
        </div>
      </section>

      <Divider />

      {/* ── WHO IT IS BUILT FOR ─────────────────────────────────────────── */}
      <section style={{ background: "#fff", padding: "72px 32px" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <Label label="WHO IT IS BUILT FOR" />
          <h2 style={{ fontFamily: SE.serif, fontSize: "clamp(1.5rem, 3vw, 2.1rem)", fontWeight: 400, margin: "16px 0 36px" }}>
            Language under pressure reveals its structure.
          </h2>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
            gap: "0",
          }}>
            {[
              { who: "Licensed operators and directors",   when: "Reading inspection reports, citations, and regulatory findings" },
              { who: "Knowledge workers and analysts",     when: "Processing dense documents, policies, and arguments at speed" },
              { who: "IP and naming researchers",          when: "Evaluating terminology through formal semantic structure (Roots mode)" },
              { who: "Writers and communicators",          when: "Auditing the precision and honesty of their own copy" },
              { who: "Anyone in a high-stakes language environment", when: "When what is said and what is meant must be distinguished" },
            ].map(({ who, when }, i) => (
              <div key={i} style={{
                padding: "24px 20px",
                borderBottom: `1px solid ${SE.soft}`,
                borderRight: i % 2 === 0 ? `1px solid ${SE.soft}` : "none",
              }}>
                <div style={{ fontFamily: SE.serif, fontSize: "0.95rem", marginBottom: "6px" }}>
                  {who}
                </div>
                <div style={{ fontSize: "0.78rem", color: SE.mid, lineHeight: 1.55 }}>
                  {when}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Divider />

      {/* ── NOT FOR YOU IF ─────────────────────────────────────────────── */}
      <section style={{ padding: "60px 32px", maxWidth: "760px", margin: "0 auto" }}>
        <Label label="NOT FOR YOU IF" />
        <div style={{ marginTop: "20px", display: "flex", flexDirection: "column", gap: "10px" }}>
          {[
            "You want a brain game that rewards guessing over analysis",
            "You need regulatory document defense right now — see Inspection Defense™ instead",
            "You are looking for passive consumption content rather than an active training environment",
            "You want an AI assistant that does the analysis for you — EMET trains you to do it yourself",
          ].map((item, i) => (
            <div key={i} style={{
              display: "flex", gap: "12px", alignItems: "flex-start",
              padding: "14px 16px", background: "#fff",
              border: `1px solid ${SE.soft}`, borderRadius: "4px",
            }}>
              <span style={{ fontFamily: SE.mono, color: SE.mid, fontSize: "0.75rem", marginTop: "2px", flexShrink: 0 }}>
                ×
              </span>
              <span style={{ fontSize: "0.85rem", color: SE.deep, lineHeight: 1.55 }}>
                {item}
              </span>
            </div>
          ))}
        </div>
      </section>

      <Divider />

      {/* ── PRICING ────────────────────────────────────────────────────── */}
      <section id="pricing" style={{ padding: "80px 32px", maxWidth: "1200px", margin: "0 auto" }}>
        <Label label="PLANS" />
        <h2 style={{ fontFamily: SE.serif, fontSize: "clamp(1.5rem, 3vw, 2.1rem)", fontWeight: 400, margin: "16px 0 8px" }}>
          Choose the training depth that matches where you are.
        </h2>
        <p style={{ color: SE.mid, fontSize: "0.9rem", marginBottom: "44px", maxWidth: "560px" }}>
          Start with Pro. Upgrade to Scholar when you want the 30-day Pattern Glow path
          and the cognitive precision report at day 30.
        </p>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          gap: "20px",
        }}>
          {TIERS.map(tier => (
            <div key={tier.key} style={{
              background: tier.featured ? SE.black : "#fff",
              border: `2px solid ${tier.featured ? SE.gold : SE.soft}`,
              borderRadius: "4px", padding: "32px",
              position: "relative",
            }}>
              {tier.featured && (
                <div style={{
                  position: "absolute", top: "16px", right: "16px",
                  background: SE.gold, color: SE.black,
                  fontFamily: SE.mono, fontSize: "0.58rem",
                  letterSpacing: "0.14em", padding: "4px 10px",
                  textTransform: "uppercase",
                }}>
                  RECOMMENDED
                </div>
              )}
              <div style={{
                fontFamily: SE.mono, fontSize: "0.6rem", letterSpacing: "0.16em",
                color: SE.gold, marginBottom: "10px", textTransform: "uppercase",
              }}>
                {tier.label}
              </div>
              <div style={{
                fontFamily: SE.serif,
                fontSize: "1.25rem",
                color: tier.featured ? SE.cream : SE.black,
                marginBottom: "4px",
              }}>
                {tier.name}
              </div>
              <div style={{
                fontFamily: SE.mono, fontSize: "1.2rem",
                color: tier.featured ? SE.cream : SE.black,
                marginBottom: "12px",
              }}>
                {tier.price}
              </div>
              <p style={{
                fontSize: "0.83rem", lineHeight: 1.65,
                color: tier.featured ? SE.mid : SE.mid,
                marginBottom: "24px",
              }}>
                {tier.desc}
              </p>
              <ul style={{ listStyle: "none", padding: 0, margin: "0 0 28px", display: "flex", flexDirection: "column", gap: "8px" }}>
                {tier.items.map((item, i) => (
                  <li key={i} style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                    <span style={{ fontFamily: SE.mono, color: SE.gold, fontSize: "0.7rem", marginTop: "3px", flexShrink: 0 }}>◆</span>
                    <span style={{ fontSize: "0.8rem", color: tier.featured ? SE.mid : SE.mid, lineHeight: 1.5 }}>{item}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={`/checkout/${tier.stripeKey}`}
                style={{
                  display: "block", textAlign: "center",
                  background: tier.featured ? SE.gold : SE.black,
                  color: tier.featured ? SE.black : SE.cream,
                  padding: "13px 24px", fontFamily: SE.sans, fontWeight: 600,
                  fontSize: "0.85rem", textDecoration: "none",
                  borderRadius: "2px", letterSpacing: "0.04em",
                }}
              >
                {tier.cta}
              </Link>
            </div>
          ))}
        </div>

        {/* Pattern Glow 30-day one-time */}
        <div style={{
          marginTop: "24px", padding: "24px 28px",
          border: `1px solid ${SE.soft}`, borderRadius: "4px",
          background: "#fff", display: "flex",
          justifyContent: "space-between", alignItems: "center",
          flexWrap: "wrap", gap: "16px",
        }}>
          <div>
            <div style={{ fontFamily: SE.serif, fontSize: "1.05rem", marginBottom: "4px" }}>
              Pattern Glow™ — 30-Day Challenge
            </div>
            <div style={{ fontSize: "0.8rem", color: SE.mid, maxWidth: "480px", lineHeight: 1.55 }}>
              One-time purchase. 30 days, one sentence per day. Mirror Bloom Report generated
              at day 30. Start here if you want a defined beginning with a concrete end.
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "16px", flexShrink: 0 }}>
            <span style={{ fontFamily: SE.mono, fontSize: "1.1rem" }}>$47</span>
            <Link
              to="/checkout/pattern_glow_challenge"
              style={{
                background: SE.black, color: SE.cream,
                padding: "11px 24px", fontFamily: SE.sans, fontWeight: 600,
                fontSize: "0.82rem", textDecoration: "none", borderRadius: "2px",
                letterSpacing: "0.04em", whiteSpace: "nowrap",
              }}
            >
              Get Pattern Glow™ →
            </Link>
          </div>
        </div>
      </section>

      <Divider />

      {/* ── PROFESSIONAL USE ───────────────────────────────────────────── */}
      <section style={{ background: SE.black, color: SE.cream, padding: "60px 32px" }}>
        <div style={{ maxWidth: "700px", margin: "0 auto", textAlign: "center" }}>
          <Label label="PROFESSIONAL USE" dark />
          <h2 style={{ fontFamily: SE.serif, fontSize: "clamp(1.4rem, 2.5vw, 1.9rem)", fontWeight: 400, margin: "16px 0 12px" }}>
            Operator Licensing and Institutional Access
          </h2>
          <p style={{ color: SE.mid, lineHeight: 1.7, marginBottom: "28px", fontSize: "0.88rem" }}>
            The Pattern Glow™ Scholar plan includes API access for organizational use.
            Multi-seat and institutional licensing available for childcare directors,
            compliance teams, and regulated operator environments.
          </p>
          <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
            <Link to="/inspection-defense" style={{
              border: `1px solid ${SE.gold}`, color: SE.gold,
              padding: "11px 24px", fontFamily: SE.sans, fontSize: "0.82rem",
              textDecoration: "none", borderRadius: "2px", letterSpacing: "0.04em",
            }}>
              Inspection Defense™ →
            </Link>
            <Link to="/for-directors" style={{
              border: `1px solid ${SE.mid}`, color: SE.mid,
              padding: "11px 24px", fontFamily: SE.sans, fontSize: "0.82rem",
              textDecoration: "none", borderRadius: "2px", letterSpacing: "0.04em",
            }}>
              Citation Calm™ →
            </Link>
          </div>
        </div>
      </section>

      {/* ── TRUST ──────────────────────────────────────────────────────── */}
      <section style={{ padding: "60px 32px", maxWidth: "1200px", margin: "0 auto" }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
          gap: "16px",
        }}>
          {TRUST.map(t => (
            <div key={t.label} style={{
              background: "#fff", border: `1px solid ${SE.soft}`,
              borderRadius: "4px", padding: "20px",
            }}>
              <div style={{
                width: "7px", height: "7px", background: SE.gold,
                borderRadius: "50%", marginBottom: "12px",
              }} />
              <div style={{
                fontFamily: SE.mono, fontSize: "0.63rem", letterSpacing: "0.1em",
                marginBottom: "8px",
              }}>
                {t.label}
              </div>
              <div style={{ fontSize: "0.77rem", color: SE.mid, lineHeight: 1.55 }}>
                {t.desc}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── FINAL CTA ──────────────────────────────────────────────────── */}
      <section style={{ padding: "80px 32px", textAlign: "center", background: SE.cream }}>
        <p style={{
          fontFamily: SE.mono, fontSize: "0.62rem", letterSpacing: "0.18em",
          color: SE.gold, marginBottom: "16px", textTransform: "uppercase",
        }}>
          BEGIN
        </p>
        <h2 style={{
          fontFamily: SE.serif, fontSize: "clamp(1.8rem, 4vw, 2.8rem)",
          fontWeight: 400, maxWidth: "560px", margin: "0 auto 24px",
        }}>
          The pattern was always there.
        </h2>
        <Link to="/app" style={{
          background: SE.black, color: SE.cream,
          padding: "16px 48px", fontFamily: SE.sans, fontWeight: 700,
          fontSize: "0.875rem", textDecoration: "none",
          borderRadius: "2px", letterSpacing: "0.06em",
          display: "inline-block",
        }}>
          Analyze A Sentence →
        </Link>
      </section>

      {/* ── FOOTER ─────────────────────────────────────────────────────── */}
      <footer style={{
        background: SE.deep, padding: "28px 32px",
        display: "flex", justifyContent: "space-between",
        alignItems: "center", flexWrap: "wrap", gap: "12px",
      }}>
        <span style={{ fontFamily: SE.mono, fontSize: "0.62rem", color: SE.mid, letterSpacing: "0.1em" }}>
          CATCH THE PATTERN™ — AN EMET / ELLARI VENTURES SYSTEM
        </span>
        <div style={{ display: "flex", gap: "20px" }}>
          {[
            { label: "ShopEllari", href: "https://shopellari.com" },
            { label: "Inspection Defense™", href: "/inspection-defense" },
            { label: "Citation Calm™", href: "/for-directors" },
            { label: "Pattern Glow™", href: "/mirror-bloom" },
          ].map(link => (
            <a key={link.label} href={link.href} style={{
              fontFamily: SE.sans, fontSize: "0.75rem",
              color: SE.mid, textDecoration: "none",
            }}>
              {link.label}
            </a>
          ))}
        </div>
      </footer>

    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────
function Label({ label, dark }: { label: string; dark?: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      <div style={{ height: "1px", width: "20px", background: "var(--se-gold, #d6b168)" }} />
      <span style={{
        fontFamily: "var(--font-mono, monospace)", fontSize: "0.6rem",
        letterSpacing: "0.18em", color: "var(--se-gold, #d6b168)",
        textTransform: "uppercase",
      }}>
        {label}
      </span>
    </div>
  );
}

function Divider() {
  return <div style={{ height: "1px", background: "var(--se-gold, #d6b168)", opacity: 0.2, margin: "0 32px" }} />;
}
